import { AfterViewInit, ChangeDetectorRef, Component, HostListener, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ADN_PLUS, EntityField } from '../../models';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { ServiceLocator } from '../../ServiceLocator';
import { breakpoints } from '../../environments/environment.breakpoints';
import { TextStyle } from '../../enums/textStyle';
import { PfComponents } from '../../enums/pfComponents';
import { ButtonType } from '../../enums/button.enum';
import { TableSelectionType, TableStateType } from '../../enums/table.enum';

interface RowActions {
  row : TableRow;
  buttonContainer : any;
}

@Component({
  selector: 'ct-table',
  templateUrl: './ct-table.component.html',
  styleUrls: ['./ct-table.component.css'],
  host: {
    '[id]': 'getId()',
    '[hidden]': 'getHidden()'
  }
})
export class CtTableComponent extends CtrlBaseComponent implements AfterViewInit {

  @ViewChild('targetWindow', { read: ViewContainerRef }) target: ViewContainerRef;
  @ViewChildren('buttonContainer', { read: ViewContainerRef }) buttonContainers: QueryList<ViewContainerRef>;
  //constants
  public rowsPerPageArr : number[] = [5,10,25,50];
  private columnResponsiveSeparator : string = '+';

  //pagination select
  public selectedOption: number = this.rowsPerPageArr[0];
  public isSelectForRowsOpen : boolean = false;

  public selectedRows: Array<TableRow> = [];
  public selected: TableRow;
  public oldSelectedADN: ADN_PLUS;
  public oldPage: number;
  public columns: Array<TableColumn> = [];
  public rows: Array<TableRow> = [];
  public actualPageRows: Array<TableRow> = [];
  public pagination: number[] = [];
  public actualPage: number;
  public columnsWithIcons = [];
  public windowOpenIndex : number = -1;

  private columnSorting: boolean;
  private rowsPerPage: number;
  private tableData: ADN_PLUS[];
  private bindingForm: string;
  private title: string;
  private contextMenuOptions: any[];
  private selectedAdn: ADN_PLUS;
  private selectedAdns: ADN_PLUS[];
  private columnsToHide: string;
  private currencyColumns: string[];
  private initPage: number = 0;
  private lastPage: number = 1;
  private currentPage: number = 0;
  private pagesToShow: number = 1;
  private currencySymbol: string = '';
  private columnsOrder: string;
  private storeSelection: boolean = false;
  private autoDeselect: boolean = true;
  private hasInlineEdition: boolean = false;
  private isRowEdited: boolean = false;
  private editCheckFailedMsg: string;
  private oldRowKey: string;
  private datePipe: DatePipe;
  private disableDeselect: boolean;
  private initialIndex: number;
  private finalIndex: number;
  private mediumColumnsOrder : string = '';
  private smallColumnsOrder : string = '';
  private isMediumSize : boolean = false;
  private isSmallSize : boolean = false;
  private isLargeSize : boolean = false;
  public rowsButtonContainer : RowActions[] = [];
  private allowSort : boolean = true;
  private sortColumn: string = '';
  private sortDirection: 'asc' | 'desc' = 'asc';
  private sortCount = 0;
  private originalRows = [];


  //selection
  private isSelectable : boolean = false; 
  private multipleSelection: boolean;
  private selectionType : TableSelectionType = TableSelectionType.Highlight;
  public isGlobalSelectionActive : boolean = false;
  private isSelectionCounterShown : boolean = false;

  //util
  public areActionsAvailable : boolean = false;
  public TableSelectionType = TableSelectionType;
  public TableStateType = TableStateType;

  //column styles
  private stateColumns : string;
  private warningStates : string;
  private dangerStates : string;
  private successStates : string;
  private primaryStates : string;
  private stateColumnsSet : Set<string> = new Set<string>();
  private warningStatesSet : Set<string> = new Set<string>();
  private dangerStatesSet : Set<string> = new Set<string>();
  private successStatesSet : Set<string> = new Set<string>();
  private primaryStatesSet : Set<string> = new Set<string>();
  private stateType: TableStateType = TableStateType.icon;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
  }

  constructor(private cdr : ChangeDetectorRef) {
    super();
    this.datePipe = ServiceLocator.injector.get<DatePipe>(DatePipe);
  }

  /**
   * Actions required after window resizing
   * @param {number} width - number - the width of the window
   */
  private manageWindowResizing(width : number): void {
    const isMediumSize = width < breakpoints.L && width >= breakpoints.M;
    const isSmallSize = width >= breakpoints.S && width < breakpoints.M;
    const isLargeSize = width >= breakpoints.L;
    
    if (isMediumSize && !this.isMediumSize) {
      this.updateSize(true, false, false);
    } else if (isSmallSize && !this.isSmallSize) {
      this.updateSize(false, true, false);
    } else if (isLargeSize && !this.isLargeSize) {
      this.updateSize(false, false, true);
    }
  }

  /**
   * This function updates the size of the table based on the size of the screen.
   * @param {boolean} isMedium 
   * @param {boolean} isSmall 
   * @param {boolean} isLarge
   */
  private updateSize(isMedium: boolean, isSmall: boolean, isLarge: boolean): void {
    this.isMediumSize = isMedium;
    this.isSmallSize = isSmall;
    this.isLargeSize = isLarge;
    this.updateTable("", false, true);
  }
  
  ngAfterViewInit(): void {
    this.onResize();
    this.paintTableMetadata();
    this.splitAndStoreColumnStyles();
  }

  private splitAndStoreColumnStyles(): void {
    if(this.getStateColumns()) this.stateColumnsSet = new Set(this.getStateColumns().split(','));
    if(this.getWarningStates()) this.warningStatesSet = new Set(this.getWarningStates().split(','));
    if(this.getDangerStates()) this.dangerStatesSet = new Set(this.getDangerStates().split(','));
    if(this.getSuccessStates()) this.successStatesSet = new Set(this.getSuccessStates().split(','));
    if(this.getPrimaryStates()) this.primaryStatesSet = new Set(this.getPrimaryStates().split(','));
  }

  paintTableMetadata() {
    let viewMetadata : any[] = [];
    this.getMetaData().models.forEach((componentMetadata : any) => {
      if(componentMetadata.ctType !== PfComponents.CtButtonCompressedContainer) viewMetadata.push(componentMetadata);
    });
    this.paintChildren({models : viewMetadata}, this.target);
  }

  /**
   * As the are not rendered at the initialization of the component, this function checks if the buttonContainer is available, 
   * puts the variable into to true so the other component can render and then paints the children of the buttonContainer.
   */
  private paintTableActions() {
    this.rowsButtonContainer = [];
    let buttonMetadata : any;
    this.getMetaData().models.forEach((componentMetadata : any) => {
      if(componentMetadata.ctType === PfComponents.CtButtonCompressedContainer) buttonMetadata = componentMetadata;
    });
    if(buttonMetadata) {
      this.areActionsAvailable = true;
      buttonMetadata.controlProperties['tableComponentId'] = this.getId();
      setTimeout(() => {    
        const buttonContainerArr : any[] = [];  
        this.buttonContainers.forEach((containerRef, index) => {
          buttonMetadata.controlProperties['rowIndex'] = index; //index of the actualPageRows shown, will be used to select the correct row for executing actions.
          buttonMetadata.controlProperties.id = buttonMetadata.controlProperties.id + index;
          buttonMetadata = this.setButtonContainerChildsToIconType(buttonMetadata);
          const buttonContainer : any = this.paintChildren({models : [buttonMetadata]}, containerRef);
          buttonContainerArr.push(buttonContainer);
        });
        this.rowsButtonContainer = this.actualPageRows.map((row, index) => {
          return {row : row, buttonContainer : buttonContainerArr[index]};
        });
        this.onActualPageRowsChange();
      });
    }
  }

  private onActualPageRowsChange() {
    let ac = this.getEvent('onActualPageRowsChange');
    if (ac) this.executeAction2(ac);
  }

  private setButtonContainerChildsToIconType(buttonMetadata: any): any {
    buttonMetadata.models.forEach((element : any, index : number) => {
      element.controlProperties.type = ButtonType.Icon;
    });
    return buttonMetadata;
  }

  /**
   * Executed by the buttonContainerComponent (for table row actions) the rowId is the rowIndex that is set to the buttonContainer in the
   * paintTableActions().
   * @param index The reason of these index is to manage which the data will be used to execute the Action (CRUD,SERVICE) 
   * for (VIEW) actions the tableId will be used.
   */
  public setSelectedRowFromButtonContainer(index : number): void {
    const row : TableRow = this.actualPageRows[index];
    this.removeSelectedRows();
    row.isSelected = true;
    this.selectedAdn = new ADN_PLUS(row.adnSource);
    this.selectedAdns = [this.selectedAdn];
    this.selected = row;
  }

  private removeSelectedRows() {
    this.rows.forEach((row) => {
      row.isSelected = false;
    });
  }

  public onRefresh(): void {
    if (this.selected) {
      this.oldSelectedADN = JSON.parse(JSON.stringify(this.selected.adnSource));//se serializa y deserializa para colonar el objeto
      this.oldPage = this.getCurrentPage();
    }
    this.selected = null;
    this.selectedRows = [];

    this.cleanFormAndListeners();
    let ac = this.getEvent('onRefresh')

    if (ac)
      this.executeAction2(ac);
  }

  public executeListener(value): void {
    if (value) {
      let ac = this.getEvent('onListener');
      this.executeAction2(ac);
    }
    this.cleanListener();
  }

  public cleanListener(): void {
    this.setDynamicProperties(this, "tableData", []);
    this.updateTable();
    if (this.bindingForm !== "")
      this.utilService.getComponentRef(this.bindingForm).cleanForm();
  }

  public cleanFormAndListeners(pAsync: boolean = false): void {
    if (this.selectedAdn)
      this.setDynamicProperties(this, "selectedAdn", null);

    if (this.selectedAdns)
      this.setDynamicProperties(this, "selectedAdns", null);

    if (this.bindingForm !== "") {
      let form = this.utilService.getComponentRef(this.bindingForm);
      if (this.bindingForm !== "" && form && form.getFormElements()) {
        if (!pAsync)
          form.cleanForm();
        else
          setTimeout(() => form.cleanForm(), 250);
      }
    }
  }

   loadPageRows(page: number): void {
    
    if (page >= 0 && page < this.pagination.length) {
      this.changePages(page);

      this.initialIndex = this.rowsPerPage * page;
      this.finalIndex = (page === 0) ? this.rowsPerPage - 1 : this.initialIndex + this.rowsPerPage - 1;
      this.actualPageRows = [];
      this.isRowEdited = false;

      for (let i = this.initialIndex; i < this.rows.length && i <= this.finalIndex; i++)
        this.actualPageRows.push(this.rows[i]);

      this.paintTableActions();
    }
  }

  /**
   * @param numOfSelectedRows : seleccion del select de paginas a escoger
   * @since: 02/09/2022
   */
  public changePagination(numOfSelectedRows : number): void {
    this.rowsPerPage = numOfSelectedRows;
    this.getTableRows();
    this.loadPageRows(this.currentPage);
  }

  /**
   * @returns numero de filas actualmente desplegadas en la tabla
   * @since: 02/09/2022
   */
  public getSelectRowsPerPage(): number {
    for(let i = 0; i < this.rowsPerPageArr.length; i++ ){
      if(this.getRowsPerPage() <= this.rowsPerPageArr[i]){
        return this.rowsPerPageArr[i];
      }
    }
    return null;
  }

  private changePages(page: number): void {
    this.currentPage = page;

    if (page - this.lastPage >= 0) {
      if (page === this.pagination.length - 1) {
        this.lastPage = this.pagination.length - 1;
        this.initPage = this.pagination.length - (this.pagesToShow + 1);
      } else {
        this.lastPage = page + 1;
        this.initPage = this.lastPage - this.pagesToShow;
      }
    } else {
      if (this.lastPage > this.pagesToShow && page <= this.initPage) {
        this.lastPage = this.lastPage - 1;
        this.initPage = page - 1;
      }
      if (page === 0) {
        this.lastPage = this.pagesToShow;
        this.initPage = 0;
      }
    }
  }

  private getTableRows(): Array<TableRow> {
    let rows = [];
    let data = this.getTableData();

    if (data) {
      for (let i = 0; i < data.length; i++) {
        let rowData = data[i];

        let row = new TableRow(i, rowData);
        row.isEditable = this.hasInlineEdition;

        for (let field of rowData.ArrayEntities.List) {
          let value = field.Value;
          let icon = null;

          if (field.Name === '#ICON#' && this.hasColumnIcon(field)) {
            let iconValues = value.split('|');
            icon = iconValues[0];
            this.columnsWithIcons[iconValues[1]] = icon;
          }

          if (value && field.Type === 12) {
            let dateValue = new Date(value.replace(/^(\d{4})(\d\d)(\d\d)(\d\d)(\d\d)(\d\d)$/, '$4:$5:$6 $2/$3/$1'));
            value = this.datePipe.transform(dateValue, 'dd/MM/yyyy hh:mma');
          }

          row.fields[field.Name] = new TableRowField(field.Type, value, icon);
        }

        rows.push(row);
      }

      this.initializeOriginalRows(rows);
      rows = this.getResponsiveRows(rows);
    }

    let auxPage = rows.length % this.getRowsPerPage();
    this.pagination = Array.from(Array(Math.floor(rows.length / this.getRowsPerPage()) + (auxPage !== 0 ? 1 : 0)).keys());

    let vViewTables = JSON.parse(sessionStorage.getItem('ViewTables'));

    if (vViewTables)
      delete vViewTables[this.getId()];
    else
      vViewTables = {};

    sessionStorage.setItem('ViewTables', JSON.stringify(vViewTables));

    return rows;
  }

  /**
   * check column for different different breakpoints
   * @param {any[]} columns - the columns array that is passed to the table component
   * @returns The columns array with the isHidden property set to true for the columns that are not in
   * the responsiveColumns set.
  */
  private getResponsiveTableColumns(columns : any[]): any[] {

    if (!this.isMediumSize && !this.isSmallSize) {
      return columns;
    }

    const responsiveColumns : Set<string> = this.getResponsiveColumnSet();

    columns.forEach((column : any) => {
      if(!responsiveColumns.has(column.propertyRef)) column.isHidden = true;
    });

    columns = this.mixColumns(columns, responsiveColumns);

    return columns;
  }
  
  /**
   * If the screen is medium or small, then mix the rows if neccesary.
   * @param {any[]} currRows - the rows that are currently being displayed in the table
   * @returns The return value is an array of objects.
   */
  private getResponsiveRows(currRows : any[]): any[] {

    if(!this.isMediumSize && !this.isSmallSize){
      return currRows;
    } 

    const responsiveColumns : Set<string> = this.getResponsiveColumnSet();

    return this.mixRows(currRows, responsiveColumns);
  }


  /**
   * pushes a new tableColumn into the columns that are gonna be displayed
   * @param {any[]} columns - the array of columns that are currently in the table
   * @param {string[]} mixedColumns - string[] = ['propertyRef','propertyRef','propertyRef'] they were separated by this.columnResponsiveSeparator
   * @returns An array of TableColumn objects.
   */
  private mixColumns(columns : any[], responsiveColumns : Set<string>): any[] {

    let first : number = -1;
    
    const responsiveColumnsArr : string[] =  Array.from(responsiveColumns);

    const mixedColumns : string[] = this.getMixedColumns(responsiveColumnsArr);

    if(mixedColumns.length === 0) return columns;
    
    const propertiesSet : Set<string> = new Set(mixedColumns[0].split(this.columnResponsiveSeparator));
    let propertyRef : string = mixedColumns[0];
    let headerTitles : string[] = [];

    columns.forEach((column : any, index : number) => {
      if(propertiesSet.has(column.propertyRef)) {
        if(first === -1) first = index;
        else {
          columns.splice(index,1);
        }
        headerTitles.push(column.headerTitle);
      }
    });

    columns[first] = new TableColumn(headerTitles.join(this.columnResponsiveSeparator),propertyRef,false);

    return columns;
  }

  /**
   * @param {any[]} currRows - any[] - this is the array of rows that need to be mixed
   * @param responsiveColumns - Set<string> - a set of columns that should be merged together
   * @returns An array of objects.
   */
  private mixRows(currRows: any[], responsiveColumns : Set<string>): any[]{

    const responsiveColumnsArr : string[] =  Array.from(responsiveColumns);
    const mixedColumns : string[] = this.getMixedColumns(responsiveColumnsArr);

    if(mixedColumns.length === 0) return currRows;

    const propertiesArr = mixedColumns[0].split(this.columnResponsiveSeparator);

    currRows.forEach((row) => {
      const newFields = {};
  
      propertiesArr.forEach((property) => {
        newFields[property] = row.fields[property];
        delete row.fields[property];
      });
  
      row.fields[propertiesArr.join(this.columnResponsiveSeparator)] = new TableRowField(
        newFields[propertiesArr[0]].type,
        propertiesArr.map(property => newFields[property].value).join(this.columnResponsiveSeparator),
        null
      );
    });
  
    return currRows;
  }

  private hasColumnIcon(props): boolean {
    let iconValues = props.Value.split('|');
    if (iconValues[1])
      return true;
    return false
  }

  public checkCurrencyColumns(columnName): boolean {
    let currencyColumns = this.getCurrencyColumns() ? this.getCurrencyColumns().split(',') : [];
    return currencyColumns.indexOf(columnName) === -1 ? true : false;
  }

  private rowClick(adn: ADN_PLUS): void {
    this.setDynamicProperties(this, "selectedAdn", adn);

    if (!this.getBindingForm() || this.getBindingForm() === "")
      return;

    let form = this.utilService.getComponentRef(this.bindingForm);

    if (!form)
      throw new Error(`No se puedo instanciar el bidingForm: ${this.getBindingForm()}`);

    form.updateBindingAdn(adn);

    if (this.storeSelection) {
      let numCliente = adn.getStringAttribute('NUM_CLIENTE');
      let docIdentificacion = adn.getStringAttribute('DOC_IDENTIFICACION');
      sessionStorage.setItem('context', `{"NUM_CLIENTE": "${numCliente}", "DOC_IDENTIFICACION": "${docIdentificacion}"}`);
    }
  }

  private getColumnsTable(): Array<TableColumn> {
    let tableData = this.getTableData();

    if (!tableData || tableData.length === 0)
      return [];

    let response = [];

    if (tableData.length > 0) {
      for (let c of tableData[0].ArrayEntities.List) {
        if (c.Name !== "#ICON#")
          response.push(new TableColumn(c.Display, c.Name, this.isColumnHidden(c.Name)));
        else {
          if (!this.hasColumnIcon(c))
            response.push(new TableColumn(c.Display, c.Name, this.isColumnHidden(c.Name)));
        }
      }
    }

    let result =  this.getResponsiveTableColumns(response);
    result = this.sortResponsiveTableColumns(result);
    return result;
  }

  private sortResponsiveTableColumns(columnsArray: any[]): any[] {
    let result = [];
    let sortedColumns : string[] = [];

    if(this.isMediumSize) sortedColumns = this.mediumColumnsOrder.split(',');
    else if(this.isSmallSize) sortedColumns = this.smallColumnsOrder.split(',');
    else sortedColumns = this.getColumnsOrder().split(',');

    if(sortedColumns.length === 1 && sortedColumns[0] === '') return columnsArray;
    sortedColumns.forEach((column : string) => {
      columnsArray.forEach((tableColumn : any) => {
        if(tableColumn.propertyRef === column) result.push(tableColumn);
      });
    });

    return result;
  }

  private isColumnHidden(element: string): boolean {
    return this.getColumnsToHide().split(',').indexOf(element) >= 0;
  }

  public updateTable(pSelectRow: any = "", pAsync: boolean = false, isResponsiveChange : boolean = false): void {
    this.isGlobalSelectionActive = false;
    if (this.selectedAdn)
      this.setDynamicProperties(this, "selectedAdn", null);

    if (this.selectedAdns)
      this.setDynamicProperties(this, "selectedAdns", null);

    if (this.storeSelection)
      sessionStorage.removeItem('context');

    this.selectedRows = [];
    this.actualPageRows = [];
    this.isRowEdited = false;

    this.columns = this.getColumnsTable();
    this.rows = this.getTableRows();

    this.cleanFormAndListeners(pAsync);
    this.loadPageRows(0);

    switch (pSelectRow) {
      case 'old':
        if (this.oldSelectedADN && this.rows && this.rows.length > 0) {
          let oldRow = this.findRowInRows(this.oldSelectedADN);

          if (oldRow)
            this.onSelect(oldRow);
          else
            this.onSelect(this.rows[0]);

          this.loadPageRows(this.oldPage);

        } else if (this.rows && this.rows.length > 0 && !this.getMetaData().controlProperties['_tableData'])
          this.onSelect(this.rows[0]);
        break;

      case 'none':
        return;

      case 'first':
      case 'true':
      default:
      case true:
        if (this.rows && this.rows.length > 0 && !this.getMetaData().controlProperties['_tableData']) {
          this.onSelect(this.rows[0]);
        }
        break;
    }
    this.paintTableActions();
    this.setRowColumnsStylesByState();
    this.cdr.detectChanges();
  }

  private setRowColumnsStylesByState() {
    if(this.stateColumnsSet.size === 0) return;
    
    this.rows.forEach((row : TableRow) => {
      this.columns.forEach((column : TableColumn) => {

        if(!this.stateColumnsSet.has(column.propertyRef)) return;

        const field = row.fields[column.propertyRef];
        const value: string = field.value;
  
        field.isWarning = this.warningStatesSet.has(value);
        field.isDanger = this.dangerStatesSet.has(value) && !field.isWarning;
        field.isSuccess = this.successStatesSet.has(value) && !field.isWarning && !field.isDanger;
        field.isPrimary = this.primaryStatesSet.has(value) && !field.isWarning && !field.isDanger && !field.isSuccess;
      });
    });
  }

  public clearTable(): void {
    this.setDynamicProperties(this, "selectedAdn", null);
    this.setDynamicProperties(this, "selectedAdns", null);
    this.selectedRows = [];
    this.columns = [];
    this.rows = [];
    this.isRowEdited = false;
    this.cleanFormAndListeners();
    this.loadPageRows(0);

    if (this.storeSelection)
      sessionStorage.removeItem('context');
  }

  public onSelect(row: TableRow): void {

    if(!this.isSelectable) return;

    if (this.isRowEdited || this.disableDeselect) {
      if (this.selected === row)
        return;
    }

    else row.isSelected = !row.isSelected;

    if (this.multipleSelection) {
      let i = this.selectedRows.indexOf(row);

      if (i != -1) {//Deselect case
        this.selectedRows.splice(i, 1);
        this.event2('onClick');
        this.event2('onDeselect');

      } else {//Select case
        this.selectedRows.push(row);
        let bindingAdn = this.utilService.deepClone(row.adnSource);

        let vViewTables = JSON.parse(sessionStorage.getItem('ViewTables'));

        if (!vViewTables)
          vViewTables = {};

        vViewTables[this.getId()] = JSON.stringify(bindingAdn);
        sessionStorage.setItem('ViewTables', JSON.stringify(vViewTables));

        this.rowClick(new ADN_PLUS(bindingAdn));
        this.event2('onSelect');
      }

      let adns = this.getAdnsFromSelectedRows();
      this.setSelectedAdns(adns);

      if (!this.selectedRows || this.selectedRows.length <= 0)
        this.cleanFormAndListeners();
    } else {
      /* En caso de que sea una "deselección". */
      if (!row.isSelected) {
        this.selected = null;
        this.selectedRows = [];
        this.oldSelectedADN = null;
        this.oldPage = null;
        this.cleanFormAndListeners();

        if (this.storeSelection)
          sessionStorage.removeItem('context');

        let vViewTables = JSON.parse(sessionStorage.getItem('ViewTables'));
        delete vViewTables[this.getId()];
        sessionStorage.setItem('ViewTables', JSON.stringify(vViewTables));

        this.event2('onClick');
        this.event2('onDeselect');

        return;
      }

      if (this.selected)
        this.selected.isSelected = false;

      this.selected = row;

      let newBindingAdn = this.utilService.deepClone(row.adnSource);
      this.rowClick(new ADN_PLUS(newBindingAdn));

      let vViewTables = JSON.parse(sessionStorage.getItem('ViewTables'));

      if (!vViewTables)
        vViewTables = {};

      vViewTables[this.getId()] = JSON.stringify(newBindingAdn);
      sessionStorage.setItem('ViewTables', JSON.stringify(vViewTables));

      this.event2('onClick');
      this.event2('onSelect');
    }
  }

  private getAdnsFromSelectedRows(): Array<ADN_PLUS> {
    let adns = new Array<ADN_PLUS>();

    this.selectedRows.forEach((row) => {
      adns.push(new ADN_PLUS(row.adnSource));
    });

    return adns;
  }

  public castCurrencySymbol(symbol: string): string {
    switch (symbol) {
      case 'CRC':
        return '₡';
      case 'USD':
        return '$';
      default:
        return '';
    }
  }

  public cleanComponentSelection(): void {
    if (!this.autoDeselect)
      return;

    super.cleanComponentSelection();
    this.cleanFormAndListeners();

    if (!this.selected)
      return;

    this.selected.isSelected = false
    this.selected = null
  }

  private findRowInRows(rowAdn: ADN_PLUS): TableRow {
    let compareValue = this.utilService.getStringAttribute(this.oldRowKey, rowAdn.ArrayEntities.List);
    return this.rows.find((r) => this.utilService.getStringAttribute(this.oldRowKey, r.adnSource.ArrayEntities.List) == compareValue);
  }

  /**
   * If the screen is medium size, return a set of the medium columns order, otherwise return a set of
   * the small columns order.
   * @returns A Set of strings.
   */
  private getResponsiveColumnSet(): Set<string> {
    return this.isMediumSize
      ? new Set(this.mediumColumnsOrder.split(','))
      : new Set(this.smallColumnsOrder.split(','));
  }

  /**
   * @param {string[]} responsiveColumnsArr - This is an array of strings that contains the columns that
   * are responsive.
   * @returns An array of strings that contain the columnResponsiveSeparator.
   */
  private getMixedColumns(responsiveColumnsArr : string[]): string[] {
    return responsiveColumnsArr.filter((col : string) => {
      if(col.includes(this.columnResponsiveSeparator)) return col;
    })
  }

  /**
   * It takes a column object and returns an array of strings, each string being a part of the column
   * header title separated by the columnResponsiveSeparator.
   * @param {any[]} col - any[] - this is the column object that is passed in from the ngFor loop in the
   * template.
   * @returns The headerTitle property of the col object.
   */
  public splitColumns(col : any[]): string[] {
    return col['headerTitle'].split(this.columnResponsiveSeparator);
  }

  /**
   * If the row value doesn't include a the separator tag, return the row value as a string array.
   * Otherwise, return the row value split by the the separator tag to fill the different values in the table.
   * @param {any} row - The row object that is being rendered.
   * @returns An array of strings.
   */
  public getRowValue(row : TableRowField) : string[] {
    let values : string[] = []
    if(row.value){
      if(!row.value.includes(this.columnResponsiveSeparator)) {
        values.push(row.value);
      } else {
        values = row.value.split(this.columnResponsiveSeparator);
      }
    } else {
      values.push('');
    }
    return values;
  }
  
  /**
   * When the user clicks on a dropdown option, the selected option is set to the value of the option
   * clicked, the dropdown is closed, and the pagination is updated.
   * @param {number} selectOptionIdx - The index of the option that was clicked.
   */
  public onSelectOptionClick(selectOptionIdx : number){
    this.selectedOption = this.rowsPerPageArr[selectOptionIdx];
    this.changeDropdownState();
    this.changePagination(this.rowsPerPageArr[selectOptionIdx]);
  }
  
  /**
   * It toggles the state of the dropdown menu.
   */
  public changeDropdownState(){
    this.isSelectForRowsOpen = !this.isSelectForRowsOpen;
  }

  /**
   * When a buttonContainer is available it shows the "acciones" text in the th element.
   */
  public getAreTableActionButtonsAvailable(): boolean {
    return true;
  }

  public getIsMultipleCheckSelectionAvailable(): boolean {
    return this.getSelectionType() === TableSelectionType.Check && Boolean(this.getMultipleSelection()) === true;
  }

  public getIsCheckSelectionAvailable(): boolean {
    return this.getSelectionType() === TableSelectionType.Check;
  }

  public selectAllRows(): void {
    this.selectedAdns = [];
    if(this.isGlobalSelectionActive) this.isGlobalSelectionActive = false;
    else this.isGlobalSelectionActive = true;
    this.rows.forEach((row) => {
      if(this.isGlobalSelectionActive && !row.isSelected) this.onSelect(row);
      else if (!this.isGlobalSelectionActive && row.isSelected) this.onSelect(row);
    });
    this.validateGlobalSelection();
  }

  public onRowSelected(event : Event, row : TableRow): void {
    event.stopPropagation();
    this.onSelect(row);
    this.validateGlobalSelection();
  }

  private validateGlobalSelection(): void {
    if(!this.selectedAdns) return;
    if(this.selectedAdns.length < this.rows.length) this.isGlobalSelectionActive = false;
    else this.isGlobalSelectionActive = true;
  }

  // Sort functionality 
  //Guarda el estado original de los rows
  private initializeOriginalRows(rows) {
    this.originalRows = [...rows];
  }

  private sortTableBy(propertyRef: string): void {
    if (this.getAllowSort()) {
      if (this.sortColumn === propertyRef) {
        this.sortCount++;

        if (this.sortCount === 3) {
      
          this.sortDirection = null; 
          this.sortColumn = ''; 
          this.sortCount = 0;
        } else {
          this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        }
      } else {
        this.sortColumn = propertyRef;
        this.sortDirection = 'asc';
        this.sortCount = 1;
      }

      this.sortRows();
    }
  }

  private sortRows(): void {
    if (this.sortDirection) {
      this.rows.sort((a, b) => {

        if (this.sortColumn.includes('PER_')) { /// Para que se utilize el sort de periodo adecuadamente, se debe de incluir el valor 'PER_' en el modelID
        const monthMapping = {
          'ENERO': 1, 'FEBRERO': 2, 'MARZO': 3, 'ABRIL': 4, 'MAYO': 5, 'JUNIO': 6,
          'JULIO': 7, 'AGOSTO': 8, 'SEPTIEMBRE': 9, 'OCTUBRE': 10, 'NOVIEMBRE': 11, 'DICIEMBRE': 12
        };
    
        const getPeriodValue = (period: string) => {
          const normalizedPeriod = period.replace(' - ', ' ').replace('-', ' ');
          const parts = normalizedPeriod.split(' ');
          const month = monthMapping[parts[0].toUpperCase()]; // Obtiene el número del mes
          const year = parseInt(parts[1], 10); // Convierte el año a número
          return year * 100 + month; // Retorna el valor del period
        };
        
        let valueA = getPeriodValue(a.fields[this.sortColumn]?.value || '');
        let valueB = getPeriodValue(b.fields[this.sortColumn]?.value || '');

        return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }else {
        let valueA = a.fields[this.sortColumn]?.value || '';
        let valueB = b.fields[this.sortColumn]?.value || '';
        let valueANum = parseFloat(valueA);
        let valueBNum = parseFloat(valueB);
        let isNumeric = !isNaN(valueANum) && !isNaN(valueBNum);
     
        if (isNumeric) {
          return this.sortDirection === 'asc' ? valueANum - valueBNum : valueBNum - valueANum;
        } else {
          return this.sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }
      }});
    }else{
      this.rows = [...this.originalRows];
    }
      this.loadPageRows(this.currentPage);
  }

  getSortIconClass(propertyRef: string): string {
    if (this.sortColumn === propertyRef) {
      switch (this.sortDirection) {
        case 'asc':
          return 'fa fa-long-arrow-up fa-lg';
        case 'desc':
          return 'fa fa-long-arrow-down fa-lg';
        case null:
          return 'fa fa-exchange fa-rotate-90 fa-lg';
        default:
          return 'fa fa-exchange fa-rotate-90 fa-lg';
      }
    } else {
      return 'fa fa-exchange fa-rotate-90 fa-lg';
    }
  }

  public validateColumnState(row : TableRow, column : TableColumn): boolean {
   if(row.fields[column.propertyRef].isDanger === true || 
    row.fields[column.propertyRef].isWarning === true || 
    row.fields[column.propertyRef].isSuccess === true ||
    row.fields[column.propertyRef].isPrimary === true){
      return true;
    } else return false;
  }

  getBlockTypeClases(field: any) {
    const isDanger = field.isDanger === true;
    const isWarning = field.isWarning === true;
    const isSuccess = field.isSuccess === true;
    const isPrimary = field.isPrimary === true;
    const shouldApplyTextWhite = isDanger || isWarning || isSuccess || isPrimary;

    return {
      'th-bg-alert': isDanger,
      'th-bg-warning': isWarning,
      'th-bg-confirmation': isSuccess,
      'th-bg-main': isPrimary,
      'th-text-white stateBlock': shouldApplyTextWhite
    };
  }
  
  getCurrentPage(): any { return this.currentPage }
  getColumnSorting(): boolean { return this.columnSorting }
  getRowsPerPage(): any { return this.rowsPerPage }
  getRows(): TableRow[] { return this.rows }
  getTableData(): ADN_PLUS[] { return this.tableData }
  getBindingForm(): string { return this.bindingForm }
  getTitle(): string { return this.title }
  getMultipleSelection(): boolean {  return this.multipleSelection }
  getContextMenuOptions(): any[] { return this.contextMenuOptions }
  getSelectedAdn(): ADN_PLUS { return this.selectedAdn }
  getSelectedAdns(): ADN_PLUS[] { return this.selectedAdns }
  getSelected(): TableRow { return this.selected }
  getColumnsToHide(): string { return this.columnsToHide }
  getCurrencyColumns(): any { return this.currencyColumns }
  getCurrencySymbol(): any { return this.currencySymbol }
  getColumnsOrder(): any { return this.columnsOrder }
  getStoreSelection(): boolean { return this.storeSelection; }
  getAutoDeselect(): boolean { return this.autoDeselect }
  getOldRowKeyt(): string { return this.oldRowKey }
  getHasInlineEdition(): boolean { return this.hasInlineEdition }
  getIsRowEdited(): boolean { return this.isRowEdited }
  getEditCheckFailedMsg(): string { return this.editCheckFailedMsg }
  getDisableDeselect(): boolean { return this.disableDeselect }
  getMediumColumnsOrder(): string { return this.mediumColumnsOrder }
  getSmallColumnsOrder(): string { return this.smallColumnsOrder }
  getIsSmallSize(): boolean { return this.isSmallSize }
  getIsMediumSize(): boolean { return this.isMediumSize}
  public get textStyle(): typeof TextStyle {
    return TextStyle; 
  }
  getIsSelectable(): boolean { return this.isSelectable }
  getSelectionType(): TableSelectionType { return this.selectionType }
  getIsSelectionCounterShown(): boolean { return this.isSelectionCounterShown }
  getStateColumns(): string { return this.stateColumns }
  getWarningStates(): string { return this.warningStates }
  getDangerStates(): string { return this.dangerStates }
  getSuccessStates(): string { return this.successStates }
  getPrimaryStates(): string {return this.primaryStates}
  getAllowSort(): boolean {return this.allowSort}

  getStateType(): TableStateType {return this.stateType}

  setCurrentPage(currentPage: any): void { this.currentPage = currentPage }
  setColumnSorting(columnSorting: boolean): void { this.columnSorting = columnSorting }
  setRowsPerPage(rowsPerPage: any) { this.rowsPerPage = rowsPerPage }
  setTableData(tableData: ADN_PLUS[]): void { this.tableData = tableData }
  setBindingForm(bindingForm: string): void { this.bindingForm = /*this.getViewId() + "_" +*/ bindingForm }
  setTitle(title: string): void { this.title = title }
  setMultipleSelection(multipleSelection: boolean): void { this.multipleSelection = multipleSelection }
  setContextMenuOptions(contextMenuOptions: any[]): void { this.contextMenuOptions = contextMenuOptions }
  setSelectedAdn(selectedAdn: ADN_PLUS): void { this.selectedAdn = selectedAdn }
  setSelectedAdns(selectedAdns: ADN_PLUS[]): void { this.selectedAdns = selectedAdns }
  setSelected(selected: TableRow): void { this.selected = selected }
  setColumnsToHide(columnsToHide: any): void { this.columnsToHide = columnsToHide }
  setCurrencyColumns(currencyColumns: any): void { this.currencyColumns = currencyColumns }
  setCurrencySymbol(currencySymbol: any): void { this.currencySymbol = currencySymbol }
  setColumnsOrder(columnsOrder: any): void { this.columnsOrder = columnsOrder }
  setStoreSelection(storeSelection: boolean): void { this.storeSelection = storeSelection; }
  setAutoDeselect(autoDeselect: boolean): void { this.autoDeselect = autoDeselect }
  setOldRowKey(oldRowKey: string): void { this.oldRowKey = oldRowKey }
  setHasInlineEdition(hasInlineEdition: boolean): void { this.hasInlineEdition = hasInlineEdition }
  setEditCheckFailedMsg(editCheckFailedMsg: string): void { this.editCheckFailedMsg = editCheckFailedMsg }
  setDisableDeselect(disableDeselect: boolean): void { this.disableDeselect = disableDeselect }
  setMediumColumnsOrder(mediumColumnsOrder : string): void { this.mediumColumnsOrder = mediumColumnsOrder}
  setSmallColumnsOrder(smallColumnsOrder : string): void { this.smallColumnsOrder = smallColumnsOrder}
  setIsSelectable(isSelectable : boolean): void { this.isSelectable = isSelectable}
  setSelectionType(selectionType : TableSelectionType): void { this.selectionType = selectionType}
  setIsSelectionCounterShown(isSelectionCounterShown : boolean): void { this.isSelectionCounterShown = isSelectionCounterShown}
  setStateColumns(stateColumns : string): void { this.stateColumns = stateColumns}
  setWarningStates(warningStates : string): void { this.warningStates = warningStates}
  setDangerStates(dangerStates : string): void { this.dangerStates = dangerStates}
  setSuccessStates(successStates : string): void { this.successStates = successStates}
  setPrimaryStates(primaryStates: string) : void { this.primaryStates = primaryStates}
  setAllowSort(allowSort: boolean): void {this.allowSort = allowSort}
  setStateType(stateType: TableStateType) : void { this.stateType = stateType}
}

export class TableColumn {
  public headerTitle: string;
  public propertyRef: string;
  public isHidden: boolean;

  constructor(headerTitle: string, propName: string, isHidden: boolean = false) {
    this.headerTitle = headerTitle;
    this.propertyRef = propName;
    this.isHidden = isHidden;
  }
}

export class TableRow {
  public rowId: number;
  public adnSource: ADN_PLUS;
  public isSelected: boolean;
  public isEditable: boolean;
  public fields: { [property: string]: TableRowField };

  constructor(rowIndx: number, adn: ADN_PLUS) {
    this.rowId = rowIndx;
    this.adnSource = adn;
    this.fields = {};
  }
}

export class TableRowField {
  public type: number;
  public value: string;
  public icon: string;
  public isSuccess : boolean;
  public isWarning : boolean;
  public isDanger : boolean;
  public isPrimary: boolean;

  constructor(type: number, value: string, icon: string = null,
    isSuccess : boolean = false, isWarning : boolean = false, isDanger : boolean = false, isPrimary: boolean = false) {
    this.type = type;
    this.value = value;
    this.icon = icon;
    this.isSuccess = isSuccess;
    this.isWarning = isWarning;
    this.isDanger = isDanger;
    this.isPrimary = isPrimary;
    
  }
}
